import { ReactComponent as FarmIcon } from 'assets/svg/portfolio/farm.svg'
import { PortfolioFarmPosition } from 'types/portfolio'
import { formatDollarAmount } from 'utils/portfolio/formatDollar'
import { getFarmPositionTotalValue } from 'utils/portfolio/getFarmPositionTotalValue'

import { FarmClaimButton } from './FarmClaimButton'
import { FarmRewardsDisplay } from './FarmRewardsDisplay'
import { PortfolioSubRowLayout } from './PortfolioSubRowLayout'
import { TokenBalanceDisplay } from './TokenBalanceDisplay'
interface FarmSubRowProps {
  position: PortfolioFarmPosition
  isLast?: boolean
  onClaimSuccess?: (providerKey: 'kodiak' | 'bgt') => void
}

export function FarmSubRow({ position, onClaimSuccess }: FarmSubRowProps) {
  const { underlyingBalances, farm, apr, formattedStakedAmount, formattedWalletBalance, isSingleFarm } = position
  const stakingToken = farm.stakingToken

  const vault = farm.vault as
    | {
        id?: string
        token0?: { symbol: string; price?: number; decimals?: number }
        token1?: { symbol: string; price?: number; decimals?: number }
      }
    | null
    | undefined

  const totalValue = getFarmPositionTotalValue(position)

  const getFarmBadge = () => {
    return (
      <span
        className="flex items-center gap-1 px-2 py-1 rounded-xs text-xs font-medium"
        style={{
          color: '#9BD4F5',
          backgroundColor: '#2B6FCA33',
        }}
      >
        <div className="w-4 h-4 [&_svg]:w-full [&_svg]:h-full [&_path]:stroke-current">
          <FarmIcon />
        </div>
        <span>Farm</span>
      </span>
    )
  }

  const renderUnderlyingBalances = () => {
    if (isSingleFarm) {
      const staked = Number(formattedStakedAmount || 0)
      const available = Number(formattedWalletBalance || 0)
      const totalAmount = staked + available
      const totalUsd = totalAmount * (stakingToken.price || 0)

      if (!totalAmount) {
        return <div className="text-kodiak-text-secondary text-right text-xs">—</div>
      }

      return (
        <div className="text-right">
          <TokenBalanceDisplay amount={totalAmount} symbol={stakingToken.symbol} usdValue={totalUsd} />
        </div>
      )
    }

    if (!underlyingBalances || !vault?.token0 || !vault?.token1) {
      return <div className="text-kodiak-text-secondary text-right text-xs">—</div>
    }

    const token0Amount = parseFloat(underlyingBalances.token0AmountFormatted || '0')
    const token1Amount = parseFloat(underlyingBalances.token1AmountFormatted || '0')
    const token0Value = token0Amount * (vault.token0.price || 0)
    const token1Value = token1Amount * (vault.token1.price || 0)

    return (
      <div className="text-right space-y-0.5">
        <div className="text-xs text-kodiak-text-primary flex items-center justify-end gap-1">
          <span>
            {underlyingBalances.token0AmountFormatted} {vault.token0.symbol}
          </span>
          <span className="text-kodiak-text-secondary">({formatDollarAmount(token0Value)})</span>
        </div>
        <div className="text-xs text-kodiak-text-primary flex items-center justify-end gap-1">
          <span>
            {underlyingBalances.token1AmountFormatted} {vault.token1.symbol}
          </span>
          <span className="text-kodiak-text-secondary">({formatDollarAmount(token1Value)})</span>
        </div>
      </div>
    )
  }

  return (
    <PortfolioSubRowLayout
      badge={getFarmBadge()}
      balances={renderUnderlyingBalances()}
      aprText={`${apr.toFixed(2)}%`}
      valueText={formatDollarAmount(totalValue)}
      rewards={
        <div className="text-right">
          <FarmRewardsDisplay farm={farm} className="text-xs" emptyClassName="text-xs" />
        </div>
      }
      actions={<FarmClaimButton onClaimSuccess={onClaimSuccess} />}
      mobileRewardsLabel="Unclaimed Rewards"
      mobileRewards={<FarmRewardsDisplay farm={farm} className="text-xs" emptyClassName="text-xs" />}
      showRewardsPlaceholder
      showActionPlaceholder
    />
  )
}
